// ─────────────────────────────────────────────────────────────────────────────
// ReportsScreen.js — Employee payslip history and P60 download
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, ScrollView, TouchableOpacity, StyleSheet, SafeAreaView, StatusBar, RefreshControl, Alert, Share } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Print from 'expo-print';
import * as Sharing from 'expo-sharing';

const API = 'https://api.jobeng.ai';
const KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const P = '#0e4e96';
const getH = async () => { const t = await AsyncStorage.getItem('auth_token').catch(() => null); return { 'Content-Type': 'application/json', 'x-api-key': KEY, ...(t ? { Authorization: `Bearer ${t}` } : {}) }; };
const fmt = v => `£${parseFloat(v || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function ReportsScreen({ navigation, route }) {
  const empId = route?.params?.employeeId || '1';
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [downloading, setDownloading] = useState(null);

  const load = useCallback(async () => {
    try {
      const h = await getH();
      const res = await fetch(`${API}/api/payslips/${parseInt(empId)||0}`, { headers: h });
      const json = await res.json().catch(() => ({}));
      if (Array.isArray(json?.data)) setPayslips(json.data);
      else {
        const cache = await AsyncStorage.getItem('payroll_payslips');
        if (cache) setPayslips(JSON.parse(cache));
      }
    } catch { }
    finally { setLoading(false); setRefreshing(false); }
  }, [empId]);

  useEffect(() => { load(); }, [load]);

  const downloadPayslip = async (ps) => {
    setDownloading(ps.id);
    try {
      const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Payslip ${ps.period}</title>
      <style>body{font-family:Arial,sans-serif;margin:40px;color:#1e293b}
      .header{text-align:center;margin-bottom:30px}.company{font-size:24px;font-weight:bold;color:#0e4e96}
      .section{margin:20px 0}.section-title{font-weight:bold;border-bottom:2px solid #0e4e96;padding-bottom:5px;margin-bottom:10px}
      table{width:100%;border-collapse:collapse}td,th{padding:10px;text-align:left;border-bottom:1px solid #e2e8f0}
      .total{font-weight:bold;background:#f8fafc}.net{font-size:18px;font-weight:bold;color:#10b981;text-align:right;margin-top:20px}
      </style></head><body>
      <div class="header"><div class="company">JobEng</div><div>Payslip — ${ps.period}</div></div>
      <div class="section"><div class="section-title">Earnings</div><table>
      <tr><td>Basic Pay</td><td style="text-align:right">${fmt(ps.gross)}</td></tr>
      <tr class="total"><td>Total Earnings</td><td style="text-align:right">${fmt(ps.gross)}</td></tr></table></div>
      <div class="section"><div class="section-title">Deductions</div><table>
      <tr><td>Income Tax</td><td style="text-align:right">${fmt(ps.tax)}</td></tr>
      <tr><td>National Insurance</td><td style="text-align:right">${fmt(ps.ni)}</td></tr>
      <tr><td>Pension</td><td style="text-align:right">${fmt(ps.pension)}</td></tr>
      <tr class="total"><td>Total Deductions</td><td style="text-align:right">${fmt((parseFloat(ps.tax||0)+parseFloat(ps.ni||0)+parseFloat(ps.pension||0)))}</td></tr></table></div>
      <div class="net">Net Pay: ${fmt(ps.net)}</div>
      <div style="margin-top:40px;text-align:center;color:#94a3b8;font-size:12px">This is a computer generated payslip. Payment Date: ${ps.payment_date ? new Date(ps.payment_date).toLocaleDateString('en-GB') : '—'}</div>
      </body></html>`;
      const { uri } = await Print.printToFileAsync({ html, base64: false });
      await Sharing.shareAsync(uri, { mimeType: 'application/pdf', dialogTitle: `Payslip ${ps.period}`, UTI: 'com.adobe.pdf' });
    } catch (e) { Alert.alert('Error', e.message); }
    finally { setDownloading(null); }
  };

  const ytdGross = payslips.reduce((s, p) => s + parseFloat(p.gross || 0), 0);
  const ytdTax = payslips.reduce((s, p) => s + parseFloat(p.tax || 0), 0);
  const ytdNI = payslips.reduce((s, p) => s + parseFloat(p.ni || 0), 0);

  return (
    <SafeAreaView style={sr.safe}>
      <StatusBar barStyle="light-content" backgroundColor={P} />
      <LinearGradient colors={[P, '#1e7be2']} style={sr.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={sr.back}><MaterialCommunityIcons name="arrow-left" size={22} color="#fff" /></TouchableOpacity>
        <Text style={sr.headerTitle}>Pay Reports</Text>
        <View style={{ width: 36 }} />
      </LinearGradient>
      <ScrollView refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); load(); }} colors={[P]} />}>
        <View style={sr.ytdCard}>
          <Text style={sr.ytdTitle}>Year to Date Summary</Text>
          <View style={sr.ytdRow}>
            {[{ label: 'Gross Pay', val: ytdGross, color: P }, { label: 'Tax', val: ytdTax, color: '#ef4444' }, { label: 'NI', val: ytdNI, color: '#f59e0b' }].map(({ label, val, color }) => (
              <View key={label} style={sr.ytdItem}>
                <Text style={[sr.ytdVal, { color }]}>{fmt(val)}</Text>
                <Text style={sr.ytdLabel}>{label}</Text>
              </View>
            ))}
          </View>
        </View>
        <View style={sr.section}>
          <Text style={sr.sectionTitle}>Payslips</Text>
          {payslips.length === 0 && !loading && <Text style={sr.empty}>No payslips available</Text>}
          {payslips.map((ps, i) => (
            <View key={ps.id || i} style={sr.payslipCard}>
              <View>
                <Text style={sr.period}>{ps.period}</Text>
                <Text style={sr.payDate}>Payment: {ps.payment_date ? new Date(ps.payment_date).toLocaleDateString('en-GB') : '—'}</Text>
              </View>
              <View style={sr.amounts}>
                <Text style={sr.gross}>{fmt(ps.gross)}</Text>
                <Text style={sr.net}>Net: {fmt(ps.net)}</Text>
              </View>
              <TouchableOpacity style={[sr.dlBtn, downloading === ps.id && { opacity: 0.5 }]} onPress={() => downloadPayslip(ps)} disabled={downloading === ps.id}>
                <MaterialCommunityIcons name={downloading === ps.id ? 'loading' : 'file-pdf-box'} size={22} color={P} />
              </TouchableOpacity>
            </View>
          ))}
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

const sr = StyleSheet.create({
  safe: { flex: 1, backgroundColor: '#f8fafc' },
  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingVertical: 14 },
  back: { padding: 4 },
  headerTitle: { color: '#fff', fontSize: 18, fontWeight: '700' },
  ytdCard: { margin: 16, backgroundColor: '#fff', borderRadius: 16, padding: 18, shadowColor: '#000', shadowOpacity: 0.06, shadowRadius: 8, elevation: 3 },
  ytdTitle: { fontSize: 14, fontWeight: '800', color: '#1e293b', marginBottom: 14 },
  ytdRow: { flexDirection: 'row' },
  ytdItem: { flex: 1, alignItems: 'center' },
  ytdVal: { fontSize: 16, fontWeight: '900' },
  ytdLabel: { fontSize: 10, color: '#64748b', fontWeight: '600', marginTop: 2 },
  section: { marginHorizontal: 16 },
  sectionTitle: { fontSize: 16, fontWeight: '800', color: '#1e293b', marginBottom: 12 },
  empty: { color: '#94a3b8', textAlign: 'center', padding: 24 },
  payslipCard: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', borderRadius: 12, padding: 14, marginBottom: 8, shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 4, elevation: 1 },
  period: { fontSize: 15, fontWeight: '700', color: '#1e293b' },
  payDate: { fontSize: 11, color: '#94a3b8', marginTop: 2 },
  amounts: { flex: 1, alignItems: 'flex-end', marginRight: 12 },
  gross: { fontSize: 14, fontWeight: '700', color: '#1e293b' },
  net: { fontSize: 12, color: '#10b981', fontWeight: '600' },
  dlBtn: { padding: 6 },
});


// ─────────────────────────────────────────────────────────────────────────────
// EmployeeMessagesScreen.js — internal messaging stub
// ─────────────────────────────────────────────────────────────────────────────
import { TextInput, KeyboardAvoidingView, Platform, FlatList } from 'react-native';

export function EmployeeMessagesScreen({ navigation }) {
  const [messages, setEmployeeMessages] = useState([
    { id: 1, from: 'HR Team', text: 'Welcome to JobEng! If you need any assistance, please reach out.', time: '09:00', read: true, avatar: 'H' },
    { id: 2, from: 'Line Manager', text: 'Great work on the Q1 project. Your performance has been noted.', time: 'Yesterday', read: true, avatar: 'M' },
    { id: 3, from: 'Payroll', text: 'Your March payslip is now available for download.', time: '2 days ago', read: false, avatar: 'P' },
  ]);
  return (
    <SafeAreaView style={sm.safe}>
      <StatusBar barStyle="light-content" backgroundColor={P} />
      <LinearGradient colors={[P, '#1e7be2']} style={sm.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={sm.back}><MaterialCommunityIcons name="arrow-left" size={22} color="#fff" /></TouchableOpacity>
        <Text style={sm.headerTitle}>Messages</Text>
        <TouchableOpacity><MaterialCommunityIcons name="pencil-outline" size={22} color="#fff" /></TouchableOpacity>
      </LinearGradient>
      <FlatList
        data={messages}
        keyExtractor={i => String(i.id)}
        renderItem={({ item }) => (
          <TouchableOpacity style={sm.msgRow} onPress={() => Alert.alert(item.from, item.text)}>
            <View style={[sm.avatar, { backgroundColor: P + '20' }]}>
              <Text style={sm.avatarText}>{item.avatar}</Text>
            </View>
            <View style={{ flex: 1, marginLeft: 12 }}>
              <View style={sm.msgTop}><Text style={sm.msgFrom}>{item.from}</Text><Text style={sm.msgTime}>{item.time}</Text></View>
              <Text style={[sm.msgText, !item.read && sm.msgTextBold]} numberOfLines={1}>{item.text}</Text>
            </View>
            {!item.read && <View style={sm.unreadDot} />}
          </TouchableOpacity>
        )}
        contentContainerStyle={{ paddingTop: 8 }}
      />
    </SafeAreaView>
  );
}

const sm = StyleSheet.create({
  safe: { flex: 1, backgroundColor: '#f8fafc' },
  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingVertical: 14 },
  back: { padding: 4 },
  headerTitle: { color: '#fff', fontSize: 18, fontWeight: '700' },
  msgRow: { flexDirection: 'row', alignItems: 'center', padding: 16, borderBottomWidth: 1, borderBottomColor: '#f1f5f9', backgroundColor: '#fff', marginBottom: 1 },
  avatar: { width: 48, height: 48, borderRadius: 24, alignItems: 'center', justifyContent: 'center' },
  avatarText: { fontSize: 18, fontWeight: '800', color: P },
  msgTop: { flexDirection: 'row', justifyContent: 'space-between' },
  msgFrom: { fontSize: 14, fontWeight: '700', color: '#1e293b' },
  msgTime: { fontSize: 11, color: '#94a3b8' },
  msgText: { fontSize: 13, color: '#64748b', marginTop: 2 },
  msgTextBold: { fontWeight: '700', color: '#1e293b' },
  unreadDot: { width: 10, height: 10, borderRadius: 5, backgroundColor: P },
});


// ─────────────────────────────────────────────────────────────────────────────
// EmployeeEditProfileScreen.js
// ─────────────────────────────────────────────────────────────────────────────
export function EmployeeEditProfileScreen({ navigation, route }) {
  const emp = route?.params?.employee || {};
  const [form, setForm] = useState({ name: emp.name || '', email: emp.email || '', phone: emp.phone || '', address: emp.address || '' });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const h = await getH();
      const id = emp.serverId || emp.id;
      if (id && !String(id).startsWith('emp_')) {
        await fetch(`${API}/api/employees/${id}`, { method: 'PUT', headers: h, body: JSON.stringify({ phone: form.phone, address: form.address }) });
      }
      Alert.alert('✅ Profile Updated');
      navigation.goBack();
    } catch (e) { Alert.alert('Error', e.message); }
    finally { setSaving(false); }
  };

  return (
    <SafeAreaView style={se.safe}>
      <StatusBar barStyle="light-content" backgroundColor={P} />
      <LinearGradient colors={[P, '#1e7be2']} style={se.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={se.back}><MaterialCommunityIcons name="arrow-left" size={22} color="#fff" /></TouchableOpacity>
        <Text style={se.headerTitle}>Edit Profile</Text>
        <TouchableOpacity onPress={save} disabled={saving}><Text style={[se.saveBtn, saving && { opacity: 0.5 }]}>{saving ? 'Saving...' : 'Save'}</Text></TouchableOpacity>
      </LinearGradient>
      <ScrollView style={{ padding: 16 }}>
        <View style={se.avatarSection}>
          <View style={se.avatar}><Text style={se.avatarText}>{(form.name || 'E')[0].toUpperCase()}</Text></View>
          <Text style={se.avatarName}>{form.name}</Text>
        </View>
        {[['Full Name', 'name', false], ['Work Email', 'email', false], ['Phone', 'phone', false], ['Address', 'address', true]].map(([label, field, multi]) => (
          <View key={field}>
            <Text style={se.label}>{label}</Text>
            <TextInput
              style={[se.input, multi && { height: 80, textAlignVertical: 'top' }]}
              value={form[field]} onChangeText={v => setForm(p => ({ ...p, [field]: v }))}
              multiline={multi}
              editable={field !== 'name' && field !== 'email'}
              placeholder={label}
            />
          </View>
        ))}
        <Text style={se.note}>Name and email can only be changed by HR. Contact payroll@jobeng.ai for updates.</Text>
      </ScrollView>
    </SafeAreaView>
  );
}

const se = StyleSheet.create({
  safe: { flex: 1, backgroundColor: '#f8fafc' },
  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingVertical: 14 },
  back: { padding: 4 },
  headerTitle: { color: '#fff', fontSize: 18, fontWeight: '700' },
  saveBtn: { color: '#fff', fontWeight: '700', fontSize: 15 },
  avatarSection: { alignItems: 'center', padding: 24 },
  avatar: { width: 80, height: 80, borderRadius: 40, backgroundColor: P + '20', alignItems: 'center', justifyContent: 'center', marginBottom: 8 },
  avatarText: { fontSize: 32, fontWeight: '900', color: P },
  avatarName: { fontSize: 18, fontWeight: '800', color: '#1e293b' },
  label: { fontSize: 12, fontWeight: '700', color: '#374151', marginBottom: 6 },
  input: { backgroundColor: '#fff', borderRadius: 10, borderWidth: 1, borderColor: '#e2e8f0', padding: 12, marginBottom: 16, fontSize: 14, color: '#1e293b' },
  note: { fontSize: 11, color: '#94a3b8', textAlign: 'center', lineHeight: 18, marginTop: 8 },
});


// ─────────────────────────────────────────────────────────────────────────────
// EmployeeSettingsScreen.js
// ─────────────────────────────────────────────────────────────────────────────
import { Switch } from 'react-native';

export function EmployeeSettingsScreen({ navigation, route }) {
  const [notifPush, setNotifPush] = useState(true);
  const [notifEmail, setNotifEmail] = useState(true);
  const [notifPayslip, setNotifPayslip] = useState(true);
  const [darkMode, setDarkMode] = useState(false);
  const [biometric, setBiometric] = useState(false);

  const SECTIONS = [
    { title: 'Notifications', items: [
      { label: 'Push Notifications', sub: 'Alerts for approvals & updates', value: notifPush, onChange: setNotifPush, icon: 'bell-outline' },
      { label: 'Email Notifications', sub: 'Weekly summaries via email', value: notifEmail, onChange: setNotifEmail, icon: 'email-outline' },
      { label: 'Payslip Alerts', sub: 'Notify when payslip is ready', value: notifPayslip, onChange: setNotifPayslip, icon: 'file-pdf-box' },
    ]},
    { title: 'Appearance', items: [
      { label: 'Dark Mode', sub: 'Switch to dark theme', value: darkMode, onChange: setDarkMode, icon: 'weather-night' },
    ]},
    { title: 'Security', items: [
      { label: 'Biometric Login', sub: 'Use Face ID / Fingerprint', value: biometric, onChange: setBiometric, icon: 'fingerprint' },
    ]},
  ];

  return (
    <SafeAreaView style={ss.safe}>
      <StatusBar barStyle="light-content" backgroundColor={P} />
      <LinearGradient colors={[P, '#1e7be2']} style={ss.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={ss.back}><MaterialCommunityIcons name="arrow-left" size={22} color="#fff" /></TouchableOpacity>
        <Text style={ss.headerTitle}>Settings</Text>
        <View style={{ width: 36 }} />
      </LinearGradient>
      <ScrollView>
        {SECTIONS.map(section => (
          <View key={section.title} style={ss.section}>
            <Text style={ss.sectionTitle}>{section.title}</Text>
            <View style={ss.card}>
              {section.items.map((item, i) => (
                <View key={item.label} style={[ss.row, i < section.items.length - 1 && ss.rowBorder]}>
                  <View style={[ss.iconBox, { backgroundColor: P + '15' }]}><MaterialCommunityIcons name={item.icon} size={20} color={P} /></View>
                  <View style={{ flex: 1, marginLeft: 12 }}>
                    <Text style={ss.rowLabel}>{item.label}</Text>
                    <Text style={ss.rowSub}>{item.sub}</Text>
                  </View>
                  <Switch value={item.value} onValueChange={item.onChange} trackColor={{ false: '#e2e8f0', true: P }} thumbColor="#fff" />
                </View>
              ))}
            </View>
          </View>
        ))}
        <View style={ss.section}>
          <Text style={ss.sectionTitle}>Support</Text>
          <View style={ss.card}>
            {[{ label: 'Contact HR', icon: 'account-tie', action: () => Alert.alert('HR Contact', 'Email: hr@jobeng.ai\nPhone: 0800 JOBENG') },
              { label: 'Contact Payroll', icon: 'cash', action: () => Alert.alert('Payroll Contact', 'Email: payroll@jobeng.ai') },
              { label: 'Help & FAQs', icon: 'help-circle-outline', action: () => {} },
              { label: 'Privacy Policy', icon: 'shield-lock-outline', action: () => {} },
              { label: 'App Version', icon: 'information-outline', action: null, value: '1.0.0' },
            ].map((item, i, arr) => (
              <TouchableOpacity key={item.label} style={[ss.row, i < arr.length - 1 && ss.rowBorder]} onPress={item.action || (() => {})}>
                <View style={[ss.iconBox, { backgroundColor: '#f1f5f9' }]}><MaterialCommunityIcons name={item.icon} size={20} color="#64748b" /></View>
                <Text style={[ss.rowLabel, { flex: 1, marginLeft: 12 }]}>{item.label}</Text>
                {item.value ? <Text style={ss.rowValue}>{item.value}</Text> : <MaterialCommunityIcons name="chevron-right" size={18} color="#94a3b8" />}
              </TouchableOpacity>
            ))}
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

const ss = StyleSheet.create({
  safe: { flex: 1, backgroundColor: '#f8fafc' },
  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingVertical: 14 },
  back: { padding: 4 },
  headerTitle: { color: '#fff', fontSize: 18, fontWeight: '700' },
  section: { marginHorizontal: 16, marginTop: 20 },
  sectionTitle: { fontSize: 12, fontWeight: '800', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 },
  card: { backgroundColor: '#fff', borderRadius: 14, overflow: 'hidden', shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 6, elevation: 2 },
  row: { flexDirection: 'row', alignItems: 'center', padding: 14 },
  rowBorder: { borderBottomWidth: 1, borderBottomColor: '#f1f5f9' },
  iconBox: { width: 38, height: 38, borderRadius: 10, alignItems: 'center', justifyContent: 'center' },
  rowLabel: { fontSize: 14, fontWeight: '600', color: '#1e293b' },
  rowSub: { fontSize: 11, color: '#94a3b8', marginTop: 1 },
  rowValue: { fontSize: 13, color: '#94a3b8', fontWeight: '600' },
});

export default ReportsScreen;
